/**
 * Cursor - Custom pixel art cursor
 */

type PixelGrid = (string | null)[][];

// Color palette
const C = {
  outline: '#1a1a1a',
  light: '#ffffff',
  mid: '#c0c0c0',
  dark: '#808080',
  skin: '#f5d0c5',
  skinDark: '#d4a594',
};

const _ = null;

// Pointing hand cursor (16x16)
const pointerHand: PixelGrid = [
  [_, _, _, _, _, C.outline, C.outline, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.outline, C.skin, C.skin, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.outline, C.skin, C.skin, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.outline, C.skin, C.skin, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, C.outline, C.skin, C.skin, C.outline, C.outline, C.outline, _, _, _, _, _, _],
  [_, _, _, _, C.outline, C.skin, C.skin, C.outline, C.skin, C.skin, C.outline, C.outline, C.outline, _, _, _],
  [_, C.outline, C.outline, _, C.outline, C.skin, C.skin, C.outline, C.skin, C.skin, C.outline, C.skin, C.skin, C.outline, _, _],
  [C.outline, C.skin, C.skin, C.outline, C.outline, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, C.skin, C.skin, C.outline, _, _],
  [C.outline, C.skinDark, C.skin, C.skin, C.outline, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _],
  [_, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _],
  [_, _, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _, _],
  [_, _, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _, _],
  [_, _, _, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _, _, _],
  [_, _, _, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.skin, C.skin, C.outline, _, _, _, _],
  [_, _, _, _, C.outline, C.skinDark, C.skin, C.skin, C.skin, C.skin, C.outline, _, _, _, _, _],
  [_, _, _, _, _, C.outline, C.outline, C.outline, C.outline, C.outline, _, _, _, _, _, _],
];

// Arrow pointer cursor (16x16)
const arrowPointer: PixelGrid = [
  [C.outline, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.outline, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.outline, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.outline, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.light, C.outline, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.light, C.light, C.outline, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.light, C.light, C.light, C.outline, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.light, C.light, C.light, C.light, C.outline, _, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.light, C.light, C.light, C.outline, C.outline, C.outline, C.outline, _, _, _, _, _, _],
  [C.outline, C.light, C.light, C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _, _, _],
  [C.outline, C.light, C.outline, _, C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _, _],
  [C.outline, C.outline, _, _, C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _, _],
  [C.outline, _, _, _, _, C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _],
  [_, _, _, _, _, C.outline, C.light, C.light, C.outline, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, C.outline, C.outline, C.outline, _, _, _, _, _, _, _],
];

// Crosshair for combat/targeting (16x16)
const crosshair: PixelGrid = [
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [C.outline, C.outline, C.outline, C.outline, _, _, _, C.light, _, _, _, C.outline, C.outline, C.outline, C.outline, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, C.outline, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

export type CursorType = 'pointer' | 'hand' | 'crosshair';

const CURSORS: { [key in CursorType]: PixelGrid } = {
  pointer: arrowPointer,
  hand: pointerHand,
  crosshair: crosshair,
};

export class Cursor {
  private currentType: CursorType = 'pointer';
  private canvas: HTMLCanvasElement;
  private visible: boolean = true;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    // Hide the default cursor
    this.canvas.style.cursor = 'none';
  }

  setCursor(type: CursorType): void {
    this.currentType = type;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.canvas.style.cursor = visible ? 'none' : 'default';
  }

  render(ctx: CanvasRenderingContext2D, mouseX: number, mouseY: number): void {
    if (!this.visible) return;

    const cursor = CURSORS[this.currentType];
    if (!cursor) return;

    const size = 16;
    const scale = 2; // 2x scale for better visibility
    const pixelSize = scale;

    // Offset for different cursor types (hotspot)
    let offsetX = 0;
    let offsetY = 0;
    if (this.currentType === 'hand') {
      offsetX = -5 * scale;
      offsetY = 0;
    } else if (this.currentType === 'crosshair') {
      offsetX = -8 * scale;
      offsetY = -8 * scale;
    }

    for (let row = 0; row < size; row++) {
      const rowData = cursor[row];
      if (!rowData) continue;

      for (let col = 0; col < rowData.length; col++) {
        const color = rowData[col];
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(
            mouseX + col * pixelSize + offsetX,
            mouseY + row * pixelSize + offsetY,
            pixelSize,
            pixelSize
          );
        }
      }
    }
  }

  getCursorType(): CursorType {
    return this.currentType;
  }
}
